import React, { ChangeEvent, FC, Fragment, SyntheticEvent, useState, useEffect } from 'react';
import {
    Accordion,
    AccordionDetails,
    Checkbox,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
} from '@mui/material';
import { useTranslation } from 'react-i18next';
import InvoiceSummary from '../invoice-summary/invoice-summary.component';
import { CollectiveInvoiceSummary, InvoiceLineItem, InvoiceNote } from 'models/invoice';
import AddIcon from '@mui/icons-material/Add';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import { Box, IconButton } from '@mui/material';
import AddLineItemDialogComponent from 'components/manage-invoices/add-line-item-dialog/add-line-item-dialog.component';
import { formatCurrency } from 'utils/format';
import { Order } from 'models/order';

type InvoiceItemProps = {
    invoice: CollectiveInvoiceSummary;
    index: number;
    totalPrice: number;
    currency: string;
    isEnabled: boolean;
    onPaymentTermChange: (paymentTerm: number, invoiceKey: string) => void;
    onInvoiceNumberChange: (invoiceNumber: string, invoiceKey: string) => void;
    onLineItemsChange: (invoiceKey: string, lineItem?: InvoiceLineItem, id?: number) => void;
    handleLineItemSelection: (invoiceKey: string, isChecked: boolean, orderId?: string, noteId?: string) => void;
    onInvoiceToggle: (invoiceKey: string, isEnabled: boolean) => void;
};
const InvoiceItem: FC<InvoiceItemProps> = ({
    invoice,
    index,
    totalPrice,
    currency,
    isEnabled,
    onPaymentTermChange,
    onInvoiceNumberChange,
    onLineItemsChange,
    handleLineItemSelection,
    onInvoiceToggle,
}: InvoiceItemProps) => {
    const { t: translate } = useTranslation(['common', 'orders']);
    const [expanded, setExpanded] = useState<number | false>(false);
    const [open, setOpen] = useState(false);
    const [noOfOrders, setNoOfOrders] = useState(0);
    const initialLineItemValue = {
        description: '',
        articleNo: '',
        quantity: 0,
        itemPrice: 0,
        itemVat: 0,
        itemVatPercentage: 0,
        totalExclVat: 0,
        totalIncVat: 0,
        isManual: true,
    };

    useEffect(() => {
        setNoOfOrders(invoice.noOfOrders || 0);
    }, [invoice]);

    const [selectedLineItem, setSelectedLineItem] = useState<InvoiceLineItem>(initialLineItemValue);

    const [invoiceLineItems, setInvoiceLineItems] = useState<Map<number, InvoiceLineItem>>(
        invoice.lineItemMap as Map<number, InvoiceLineItem>,
    );

    const handleAccordion = (panelIndex: number) => (event: SyntheticEvent, isExpanded: boolean) => {
        setExpanded(isExpanded ? panelIndex : false);
    };

    const openAddLineItemModal = () => {
        setOpen(true);
    };

    const openModalToEdit = (lineItem: InvoiceLineItem) => {
        setSelectedLineItem(lineItem);
        setOpen(true);
    };

    const onDialogClose = () => {
        setOpen(false);
        setSelectedLineItem(initialLineItemValue);
    };

    const deleteLineItem = (id: number) => {
        const newLineItems = new Map(invoiceLineItems);
        onLineItemsChange(invoice.key, undefined, id);
        newLineItems.delete(id);
        setInvoiceLineItems(newLineItems);
    };

    const onLineAdd = (lineItem: InvoiceLineItem) => {
        const newLineItems = new Map(invoiceLineItems);
        let valueToSet;
        if (lineItem.id) {
            valueToSet = { ...lineItem };
            newLineItems.set(lineItem.id as number, { ...lineItem });
        } else {
            const id = newLineItems.size + 1;
            valueToSet = { ...lineItem, id };
            newLineItems.set(id, valueToSet);
        }
        setInvoiceLineItems(newLineItems);
        onLineItemsChange(invoice.key, valueToSet);
        setOpen(false);
        setSelectedLineItem(initialLineItemValue);
    };

    const handleCheckboxChange = (isChecked: boolean, orderId?: string, noteId?: string) => {
        if (orderId) {
            setNoOfOrders(isChecked ? noOfOrders + 1 : noOfOrders - 1);
        }
        handleLineItemSelection(invoice.key, isChecked, orderId, noteId);
    };

    return (
        <Accordion key={index} expanded={expanded === index} onChange={handleAccordion(index)}>
            <InvoiceSummary
                onInvoiceNumberChange={onInvoiceNumberChange}
                onPaymentTermChange={onPaymentTermChange}
                invoice={invoice}
                totalPrice={totalPrice}
                currency={currency}
                noOfOrders={noOfOrders}
                isEnabled={isEnabled}
                onInvoiceToggle={(enabled) => onInvoiceToggle(invoice.key, enabled)}
            />
            <AccordionDetails sx={{ opacity: isEnabled ? 1 : 0.5, pointerEvents: isEnabled ? 'auto' : 'none' }}>
                <TableContainer>
                    <Table size="small">
                        <TableHead>
                            <TableRow>
                                <TableCell></TableCell>
                                <TableCell>{translate('common:DESCRIPTION')}</TableCell>
                                <TableCell>{translate('orders:QUANTITY')}</TableCell>
                                <TableCell>{translate('common:PRICE')}</TableCell>
                                <TableCell>{translate('orders:VAT')}</TableCell>
                                <TableCell align="right">{translate('orders:TOTAL_INCL_VAT')}</TableCell>
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            {Array.from(invoiceLineItems?.values() || []).map((lineItem, index) => {
                                return (
                                    <TableRow key={index}>
                                        <TableCell sx={{ maxWidth: 10, padding: 0 }}>
                                            {!lineItem.isManual && lineItem.isIdentifyingRow ? (
                                                <Checkbox
                                                    color="secondary"
                                                    defaultChecked
                                                    onChange={(event: ChangeEvent<HTMLInputElement>) =>
                                                        handleCheckboxChange(
                                                            event.target.checked,
                                                            (lineItem.order as Order)?._id,
                                                            (lineItem.note as InvoiceNote)?._id,
                                                        )
                                                    }
                                                />
                                            ) : undefined}
                                        </TableCell>
                                        <TableCell>
                                            <Box display="flex" alignItems="center">
                                                <Box mr={1}>{lineItem.description}</Box>
                                                {lineItem.isManual ? (
                                                    <Fragment>
                                                        <IconButton
                                                            size="small"
                                                            onClick={() => openModalToEdit(lineItem)}
                                                        >
                                                            <EditIcon fontSize="small" />
                                                        </IconButton>
                                                        <IconButton
                                                            size="small"
                                                            onClick={() => deleteLineItem(lineItem.id as number)}
                                                        >
                                                            <DeleteIcon fontSize="small" />
                                                        </IconButton>
                                                    </Fragment>
                                                ) : undefined}
                                            </Box>
                                        </TableCell>
                                        <TableCell>{lineItem.quantity || '-'}</TableCell>
                                        <TableCell>{formatCurrency(lineItem.itemPrice, lineItem?.currency)}</TableCell>
                                        <TableCell>{formatCurrency(lineItem.itemVat, lineItem?.currency)}</TableCell>
                                        <TableCell align="right">
                                            {formatCurrency(lineItem.totalIncVat, lineItem?.currency)}
                                        </TableCell>
                                    </TableRow>
                                );
                            })}
                        </TableBody>
                    </Table>
                    <IconButton onClick={openAddLineItemModal} aria-label="add-row">
                        <AddIcon />
                    </IconButton>
                </TableContainer>
            </AccordionDetails>
            <AddLineItemDialogComponent
                lineItem={selectedLineItem}
                open={open}
                onDialogClose={onDialogClose}
                onLineAdd={onLineAdd}
            />
        </Accordion>
    );
};

export default InvoiceItem;
